'use client';

import { createContext, useContext } from 'react';

/**
 * Context value for Form-level state that needs to be propagated to all form controls
 */
export type FormContextValue = {
  /** Whether all form fields should be disabled */
  disabled: boolean;
  /** Whether submit buttons should be disabled (e.g., form is pristine and disableSubmitUntilDirty is enabled) */
  submitDisabled: boolean;
};

const defaultContextValue: FormContextValue = {
  disabled: false,
  submitDisabled: false,
};

/**
 * Context for propagating form-level state to all form controls
 */
export const FormContext = createContext<FormContextValue>(defaultContextValue);

/**
 * Hook to access form context values
 * @returns {FormContextValue} The current form context
 * @example
 * ```tsx
 * const { disabled } = useFormContext();
 * const isDisabled = field.disabled || disabled;
 * ```
 */
export const useFormContext = (): FormContextValue => {
  return useContext(FormContext);
};
